import { compileProject } from './language.js';
import { saveSb3 } from './sb3.js';
import { decodeWav } from './assets.js';
import { menuValue } from './catalog.js';

const STAGE_W = 480;
const STAGE_H = 360;

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function str(v) { return v == null ? '' : String(v); }
function bool(v) {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  const s = String(v).toLowerCase();
  return s !== '' && s !== '0' && s !== 'false';
}
function eq(a, b) {
  const na = Number(a), nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
  return String(a).toLowerCase() === String(b).toLowerCase();
}

function scratchKey(e) {
  const map = {
    ' ': 'space', Space: 'space',
    ArrowLeft: 'left arrow', ArrowRight: 'right arrow',
    ArrowUp: 'up arrow', ArrowDown: 'down arrow',
    Enter: 'enter', Escape: 'escape',
    Shift: 'shift', Control: 'control',
    Backspace: 'backspace', Delete: 'delete',
  };
  if (map[e.key] || map[e.code]) return map[e.key] || map[e.code];
  if (e.key.length === 1) return e.key.toLowerCase();
  return e.key.toLowerCase();
}

export class Player {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = null;
    this.project = null;
    this.running = false;
    this._turbo = false;
    this.threads = [];
    this.clones = [];
    this.timers = { start: performance.now() };
    this.mouse = { x: 0, y: 0, down: false };
    this.keys = new Set();
    this.answer = '';
    this.ask = null;
    this.audio = null;
    this.images = new Map();
    this.pen = document.createElement('canvas');
    this.pen.width = STAGE_W;
    this.pen.height = STAGE_H;
    this.penCtx = this.pen.getContext('2d');
    this.broadcasts = new Map();
    this.monitors = [];
    this.raf = 0;
    this.edgeYield = 0;
    this.onAsk = null;
    this.onMonitors = null;
    this.engineName = 'Scratch';
    this.vm = null;
    this.renderer = null;
    this._vmFailed = false;
    this.bindInput();
  }

  get turbo() { return this._turbo; }
  set turbo(v) {
    this._turbo = !!v;
    this.vm?.setTurboMode(this._turbo);
  }

  bindInput() {
    const c = this.canvas;
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * STAGE_W - STAGE_W / 2;
      const y = STAGE_H / 2 - ((e.clientY - r.top) / r.height) * STAGE_H;
      this.mouse.x = x;
      this.mouse.y = y;
      if (this.vm) {
        this.vm.postIOData('mouse', {
          x: e.clientX - r.left,
          y: e.clientY - r.top,
          canvasWidth: r.width,
          canvasHeight: r.height,
        });
      }
    };
    c.addEventListener('mousemove', pos);
    c.addEventListener('mousedown', (e) => {
      pos(e);
      this.mouse.down = true;
      if (this.vm) {
        const r = c.getBoundingClientRect();
        this.vm.postIOData('mouse', {
          isDown: true,
          x: e.clientX - r.left,
          y: e.clientY - r.top,
          canvasWidth: r.width,
          canvasHeight: r.height,
        });
      } else this.clickAt();
    });
    window.addEventListener('mouseup', (e) => {
      this.mouse.down = false;
      if (this.vm) {
        const r = c.getBoundingClientRect();
        this.vm.postIOData('mouse', {
          isDown: false,
          x: e.clientX - r.left,
          y: e.clientY - r.top,
          canvasWidth: r.width,
          canvasHeight: r.height,
        });
      }
    });
    window.addEventListener('keydown', (e) => {
      if (e.target.matches('textarea, input')) return;
      if (this.vm) {
        this.vm.postIOData('keyboard', { keyCode: e.keyCode, key: e.key, isDown: true });
        return;
      }
      const k = scratchKey(e);
      this.keys.add(k);
      this.keys.add('any');
      this.startHats('event_whenkeypressed', (b) => (b.fields.KEY_OPTION?.[0] || '') === k || b.fields.KEY_OPTION?.[0] === 'any');
    });
    window.addEventListener('keyup', (e) => {
      if (this.vm) {
        this.vm.postIOData('keyboard', { keyCode: e.keyCode, key: e.key, isDown: false });
        return;
      }
      this.keys.delete(scratchKey(e));
    });
  }

  async ensureVM() {
    if (this.vm) return true;
    if (this._vmFailed) return false;
    try {
      const vmMod = await import('scratch-vm');
      const storageMod = await import('scratch-storage');
      const renderMod = await import('scratch-render');
      const svgMod = await import('scratch-svg-renderer');
      const VM = vmMod.default || vmMod;
      const Storage = storageMod.default || storageMod.ScratchStorage;
      const Render = renderMod.default || renderMod;
      const BitmapAdapter = svgMod.BitmapAdapter || svgMod.default?.BitmapAdapter;
      const vm = new VM();
      const storage = new Storage();
      vm.attachStorage(storage);
      const renderer = new Render(this.canvas);
      try { renderer.setLayerGroupOrdering(['background', 'video', 'pen', 'sprite']); } catch { /* older renderers */ }
      renderer.setBackgroundColor(1, 1, 1);
      vm.attachRenderer(renderer);
      if (BitmapAdapter) vm.attachV2BitmapAdapter(new BitmapAdapter());
      try {
        const audioMod = await import('scratch-audio');
        const AudioEngine = audioMod.default || audioMod;
        vm.attachAudioEngine(new AudioEngine());
      } catch { /* audio optional */ }
      vm.setCompatibilityMode(false);
      renderer.resize(STAGE_W, STAGE_H);
      vm.on('PROJECT_RUN_START', () => { this.running = true; });
      vm.on('PROJECT_RUN_STOP', () => { this.running = false; });
      vm.runtime.on('QUESTION', (q) => {
        this.onAsk?.(q == null ? null : { question: q || '' });
      });
      vm.on('MONITORS_UPDATE', (list) => {
        if (!this.onMonitors) return;
        const mons = [];
        list.forEach((mon) => {
          if (!mon.visible) return;
          mons.push({
            x: mon.x, y: mon.y,
            params: mon.params || {},
            value: mon.value,
          });
        });
        this.onMonitors(mons);
      });
      window.addEventListener('resize', () => renderer.resize(STAGE_W, STAGE_H));
      vm.start();
      this.vm = vm;
      this.renderer = renderer;
      this.engineName = 'Scratch 3 (WebGL)';
      return true;
    } catch (e) {
      console.warn('scratch-vm renderer unavailable, using canvas fallback', e);
      this._vmFailed = true;
      this.ctx = this.canvas.getContext('2d');
      return false;
    }
  }

  async load(project) {
    this.stop();
    compileProject(project);
    this.project = project;
    this.clones = [];
    this.penCtx.clearRect(0, 0, STAGE_W, STAGE_H);
    if (await this.ensureVM()) {
      const data = await saveSb3(project);
      await this.vm.loadProject(data);
      this.vm.setTurboMode(this._turbo);
      this.syncProjectToVm();
      this.renderer.resize(STAGE_W, STAGE_H);
      this.renderer.draw();
      this.engineName = 'Scratch 3 (WebGL)';
      return;
    }
    if (!this.ctx) this.ctx = this.canvas.getContext('2d');
    await this.preloadImages();
    this.draw();
  }

  async recompile(project) {
    compileProject(project);
    this.project = project;
    if (this.vm) {
      if (this.hotSwapScripts(project)) {
        this.renderer.resize(STAGE_W, STAGE_H);
        this.renderer.draw();
        this.engineName = 'Scratch 3 (WebGL)';
        return;
      }
      const snap = this.snapshotVmState();
      const data = await saveSb3(project);
      await this.vm.loadProject(data);
      this.vm.setTurboMode(this._turbo);
      this.restoreVmState(snap);
      this.renderer.resize(STAGE_W, STAGE_H);
      this.renderer.draw();
      this.engineName = 'Scratch 3 (WebGL)';
      return;
    }
    this.threads = [];
    this.clones = [];
    this.running = false;
    if (!this.ctx) this.ctx = this.canvas.getContext('2d');
    await this.preloadImages();
    this.draw();
  }

  findVmTarget(t) {
    if (!this.vm) return null;
    return this.vm.runtime.targets.find((x) => (x.isStage && t.isStage)
      || x.getName?.() === t.name || x.sprite?.name === t.name) || null;
  }

  hotSwapScripts(project) {
    try {
      const originals = this.vm.runtime.targets.filter((x) => x.isOriginal !== false);
      if (originals.length !== project.targets.length) return false;
      for (const t of project.targets) {
        if (!this.findVmTarget(t)) return false;
      }
      for (const t of project.targets) {
        const vt = this.findVmTarget(t);
        if (!vt?.blocks?.deleteAllBlocks || !vt.blocks.createBlock) return false;
        vt.blocks.deleteAllBlocks();
        for (const [id, b] of Object.entries(t.blocks || {})) {
          vt.blocks.createBlock(JSON.parse(JSON.stringify({ ...b, id })));
        }
        vt.blocks.resetCache?.();
      }
      const rt = this.vm.runtime;
      if (Array.isArray(rt.threads)) {
        rt.threads = rt.threads.filter((th) => {
          const id = typeof th.peekStack === 'function' ? th.peekStack() : th.stack?.[th.stack.length - 1];
          if (id && th.target?.blocks?.getBlock?.(id)) return true;
          try { rt._stopThread?.(th); } catch { /* */ }
          return false;
        });
      }
      return true;
    } catch (e) {
      console.warn('hot-swap scripts failed, falling back to reload', e);
      return false;
    }
  }

  snapshotVmState() {
    if (!this.vm) return null;
    const targets = [];
    for (const t of this.vm.runtime.targets) {
      if (t.isOriginal === false) continue;
      const vars = {};
      for (const v of Object.values(t.variables || {})) {
        vars[v.name] = { value: v.value, type: v.type };
      }
      targets.push({
        name: t.getName?.() || t.sprite?.name || '',
        isStage: !!t.isStage,
        x: t.x, y: t.y,
        direction: t.direction,
        size: t.size,
        visible: t.visible,
        rotationStyle: t.rotationStyle,
        currentCostume: t.currentCostume,
        volume: t.volume,
        effects: t.effects ? { ...t.effects } : {},
        vars,
      });
    }
    let timer = 0;
    try { timer = this.vm.runtime.ioDevices.clock.projectTimer(); } catch { /* */ }
    return { targets, timer };
  }

  restoreVmState(snap) {
    if (!this.vm || !snap) return;
    for (const s of snap.targets) {
      const t = this.vm.runtime.targets.find((x) => (x.isStage && s.isStage)
        || x.getName?.() === s.name || x.sprite?.name === s.name);
      if (!t) continue;
      if (!s.isStage) {
        t.setXY?.(s.x || 0, s.y || 0);
        t.setSize?.(s.size || 100);
        t.setVisible?.(s.visible !== false);
        t.setDirection?.(s.direction || 90);
        if (s.rotationStyle && t.setRotationStyle) t.setRotationStyle(s.rotationStyle);
      }
      if (typeof s.currentCostume === 'number' && t.setCostume) t.setCostume(s.currentCostume);
      if (s.effects && t.setEffect) {
        for (const [k, v] of Object.entries(s.effects)) t.setEffect(k, v);
      }
      for (const [name, rec] of Object.entries(s.vars || {})) {
        const v = Object.values(t.variables || {}).find((x) => x.name === name);
        if (v) v.value = rec.value;
      }
    }
    try {
      const clock = this.vm.runtime.ioDevices.clock;
      const t = clock?._projectTimer;
      if (t && typeof snap.timer === 'number' && typeof t.now === 'function') {
        t.startTime = t.now() - snap.timer * 1000;
      }
    } catch { /* timer restore is best-effort */ }
  }

  async preloadImages() {
    const keep = new Set();
    for (const t of this.project.targets) {
      for (const c of t.costumes) {
        const key = c.md5ext || c.assetId;
        keep.add(key);
        if (this.images.has(key)) continue;
        const img = await this.costumeToImage(c);
        if (img) this.images.set(key, img);
      }
    }
    for (const key of [...this.images.keys()]) {
      if (!keep.has(key)) this.images.delete(key);
    }
  }

  async cacheCostume(c) {
    if (this.vm && this.project) {
      const t = this.project.targets.find((x) => x.costumes?.includes(c));
      const name = t?.name;
      const vt = this.vm.runtime.targets.find((x) => x.getName?.() === name || x.sprite?.name === name);
      if (vt && t) {
        this.vm.setEditingTarget(vt.id);
        const idx = t.costumes.indexOf(c);
        if (idx >= 0 && c.dataFormat === 'svg' && c._svg) {
          this.vm.updateSvg(idx, c._svg, c.rotationCenterX, c.rotationCenterY);
        }
      }
      this.renderer?.draw();
      return;
    }
    const key = c.md5ext || c.assetId;
    const img = await this.costumeToImage(c);
    if (img) this.images.set(key, img);
    if (this.project) this.draw();
  }

  syncProjectToVm() {
    if (!this.vm || !this.project) return;
    for (const t of this.project.targets) {
      const vt = this.vm.runtime.targets.find((x) => (x.isStage && t.isStage) || x.getName?.() === t.name || x.sprite?.name === t.name);
      if (!vt) continue;
      if (!t.isStage) {
        vt.setXY?.(t.x || 0, t.y || 0);
        vt.setSize?.(t.size || 100);
        vt.setVisible?.(t.visible !== false);
        vt.setDirection?.(t.direction || 90);
        if (t.rotationStyle && vt.setRotationStyle) vt.setRotationStyle(t.rotationStyle);
      }
      if (typeof t.currentCostume === 'number' && vt.setCostume) vt.setCostume(t.currentCostume);
    }
  }

  costumeToImage(c) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      if (c.dataFormat === 'svg' && c._svg) {
        const blob = new Blob([svgForImage(c._svg)], { type: 'image/svg+xml;charset=utf-8' });
        img.src = URL.createObjectURL(blob);
      } else if (c._data) {
        const type = c.dataFormat === 'svg' ? 'image/svg+xml;charset=utf-8' : `image/${c.dataFormat}`;
        const data = c.dataFormat === 'svg' ? svgForImage(new TextDecoder().decode(c._data)) : c._data;
        img.src = URL.createObjectURL(new Blob([data], { type }));
      } else resolve(null);
    });
  }

  greenFlag() {
    if (!this.project) return;
    if (this.vm) {
      this.running = true;
      this.vm.greenFlag();
      return;
    }
    this.stopThreads();
    this.timers.start = performance.now();
    this.startHats('event_whenflagclicked');
    this.start();
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this.raf);
    this.stopThreads();
    this.ask = null;
    if (this.onAsk) this.onAsk(null);
    if (this.vm) {
      this.vm.stopAll();
      this.renderer?.draw();
      return;
    }
    this.stopSounds();
    if (this.ctx && this.project) this.draw();
  }

  stopThreads() {
    this.threads = [];
    this.clones = this.clones.filter(() => false);
  }

  start() {
    if (this.running) return;
    this.running = true;
    const loop = () => {
      if (!this.running) return;
      this.step();
      this.draw();
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  startHats(opcode, pred) {
    if (!this.project) return;
    const targets = [...this.project.targets, ...this.clones];
    for (const t of targets) {
      for (const [id, b] of Object.entries(t.blocks || {})) {
        if (!b.topLevel || b.opcode !== opcode) continue;
        if (pred && !pred(b, t)) continue;
        this.threads.push(this.makeThread(t, id));
      }
    }
  }

  makeThread(target, blockId) {
    return {
      target,
      stack: [{ id: blockId, phase: 'enter' }],
      waitUntil: 0,
      warp: false,
      done: false,
    };
  }

  clickAt() {
    const hit = this.hitSprite(this.mouse.x, this.mouse.y);
    if (hit?.isStage) this.startHats('event_whenstageclicked');
    else if (hit) {
      for (const [id, b] of Object.entries(hit.blocks || {})) {
        if (b.topLevel && b.opcode === 'event_whenthisspriteclicked') {
          this.threads.push(this.makeThread(hit, id));
        }
      }
      this.start();
    }
  }

  hitSprite(x, y) {
    const sprites = this.drawableTargets().filter((t) => !t.isStage && t.visible !== false);
    for (let i = sprites.length - 1; i >= 0; i--) {
      const t = sprites[i];
      const box = this.spriteBox(t);
      if (x >= box.l && x <= box.r && y >= box.b && y <= box.t) return t;
    }
    return this.project.targets[0];
  }

  drawableTargets() {
    const list = [...this.project.targets.filter((t) => t.isStage), ...this.project.targets.filter((t) => !t.isStage), ...this.clones];
    return list.sort((a, b) => (a.layerOrder || 0) - (b.layerOrder || 0));
  }

  spriteBox(t) {
    const c = t.costumes[t.currentCostume] || t.costumes[0];
    const img = c && this.images.get(c.md5ext || c.assetId);
    const res = c?.bitmapResolution || 1;
    const w = ((img?.naturalWidth || 80) / res) * (t.size || 100) / 100;
    const h = ((img?.naturalHeight || 80) / res) * (t.size || 100) / 100;
    return { l: t.x - w / 2, r: t.x + w / 2, b: t.y - h / 2, t: t.y + h / 2, w, h };
  }

  step() {
    const limit = this.turbo ? 1000 : 80;
    let steps = 0;
    for (const th of this.threads) {
      if (th.done) continue;
      if (th.waitUntil && performance.now() < th.waitUntil) continue;
      th.waitUntil = 0;
      while (!th.done && steps < limit) {
        if (!this.advance(th)) break;
        steps++;
        if (!this.turbo && th.yieldNow) { th.yieldNow = false; break; }
      }
    }
    this.threads = this.threads.filter((t) => !t.done);
    if (this.onMonitors) this.onMonitors(this.collectMonitors());
  }

  advance(th) {
    const frame = th.stack[th.stack.length - 1];
    if (!frame) { th.done = true; return false; }
    if (frame.phase === 'loop') frame.phase = 'enter';
    const b = th.target.blocks[frame.id];
    if (!b) { th.done = true; return false; }
    if (frame.phase === 'enter') {
      const r = this.exec(th, b, frame);
      if (r === 'wait-retry') return false;
      if (r === 'wait') {
        frame.phase = 'waiting';
        return false;
      }
      if (r === 'push') return true;
      frame.phase = 'next';
    }
    if (frame.phase === 'waiting') {
      if (frame.glide && frame.glide.ms) {
        const k = Math.min(1, (performance.now() - frame.glide.t0) / frame.glide.ms);
        this.setXY(th.target, frame.glide.x0 + (frame.glide.x1 - frame.glide.x0) * k,
          frame.glide.y0 + (frame.glide.y1 - frame.glide.y0) * k);
        if (k < 1) return false;
      }
      frame.phase = 'next';
    }
    if (frame.phase === 'next') {
      if (b.next) {
        frame.id = b.next;
        frame.phase = 'enter';
        return true;
      }
      th.stack.pop();
      if (!th.stack.length) { th.done = true; return false; }
      const parent = th.stack[th.stack.length - 1];
      parent.phase = parent.afterSub || 'next';
      return true;
    }
    return true;
  }

  exec(th, b, frame) {
    const t = th.target;
    const op = b.opcode;
    const In = (name) => this.evalInput(th, b.inputs[name]);
    const F = (name) => b.fields[name]?.[0];

    if (op === 'event_whenflagclicked' || op === 'event_whenkeypressed' || op === 'event_whenthisspriteclicked'
      || op === 'event_whenstageclicked' || op === 'event_whenbackdropswitchesto'
      || op === 'event_whenbroadcastreceived' || op === 'control_start_as_clone'
      || op === 'event_whengreaterthan' || op === 'procedures_definition') {
      return 'next';
    }
    if (op === 'motion_movesteps') { this.move(t, num(In('STEPS'))); return 'next'; }
    if (op === 'motion_turnright') { t.direction = this.wrapDir((t.direction || 90) + num(In('DEGREES'))); return 'next'; }
    if (op === 'motion_turnleft') { t.direction = this.wrapDir((t.direction || 90) - num(In('DEGREES'))); return 'next'; }
    if (op === 'motion_gotoxy') { this.setXY(t, num(In('X')), num(In('Y'))); return 'next'; }
    if (op === 'motion_goto') { const p = this.gotoPoint(t, In('TO')); this.setXY(t, p.x, p.y); return 'next'; }
    if (op === 'motion_glidesecstoxy' || op === 'motion_glideto') {
      const secs = num(In('SECS'));
      const dest = op === 'motion_glidesecstoxy' ? { x: num(In('X')), y: num(In('Y')) } : this.gotoPoint(t, In('TO'));
      frame.glide = { x0: t.x, y0: t.y, x1: dest.x, y1: dest.y, t0: performance.now(), ms: Math.max(0, secs * 1000) };
      th.waitUntil = performance.now() + frame.glide.ms;
      return secs > 0 ? 'wait' : 'next';
    }
    if (op === 'motion_pointindirection') { t.direction = this.wrapDir(num(In('DIRECTION'))); return 'next'; }
    if (op === 'motion_pointtowards') {
      const p = this.gotoPoint(t, In('TOWARDS'));
      t.direction = this.wrapDir(Math.atan2(p.x - t.x, p.y - t.y) * 180 / Math.PI);
      return 'next';
    }
    if (op === 'motion_changexby') { this.setXY(t, t.x + num(In('DX')), t.y); return 'next'; }
    if (op === 'motion_setx') { this.setXY(t, num(In('X')), t.y); return 'next'; }
    if (op === 'motion_changeyby') { this.setXY(t, t.x, t.y + num(In('DY'))); return 'next'; }
    if (op === 'motion_sety') { this.setXY(t, t.x, num(In('Y'))); return 'next'; }
    if (op === 'motion_ifonedgebounce') { this.bounce(t); return 'next'; }
    if (op === 'motion_setrotationstyle') { t.rotationStyle = F('STYLE'); return 'next'; }

    if (op === 'looks_sayforsecs' || op === 'looks_thinkforsecs') {
      t._bubble = { text: str(In('MESSAGE')), think: op.includes('think') };
      const secs = num(In('SECS'));
      th.waitUntil = performance.now() + secs * 1000;
      frame._clearBubble = true;
      setTimeout(() => { if (t._bubble) t._bubble = null; }, secs * 1000);
      return 'wait';
    }
    if (op === 'looks_say' || op === 'looks_think') { t._bubble = { text: str(In('MESSAGE')), think: op.includes('think') }; return 'next'; }
    if (op === 'looks_switchcostumeto') { this.switchCostume(t, In('COSTUME')); return 'next'; }
    if (op === 'looks_nextcostume') { t.currentCostume = (t.currentCostume + 1) % t.costumes.length; return 'next'; }
    if (op === 'looks_switchbackdropto' || op === 'looks_switchbackdroptoandwait') {
      const stage = this.stage();
      this.switchCostume(stage, In('BACKDROP'));
      this.startHats('event_whenbackdropswitchesto', (blk) => blk.fields.BACKDROP?.[0] === stage.costumes[stage.currentCostume]?.name);
      return 'next';
    }
    if (op === 'looks_nextbackdrop') {
      const stage = this.stage();
      stage.currentCostume = (stage.currentCostume + 1) % stage.costumes.length;
      return 'next';
    }
    if (op === 'looks_changesizeby') { t.size = Math.max(1, (t.size || 100) + num(In('CHANGE'))); return 'next'; }
    if (op === 'looks_setsizeto') { t.size = Math.max(1, num(In('SIZE'))); return 'next'; }
    if (op === 'looks_changeeffectby') { t.effects = t.effects || {}; t.effects[F('EFFECT')] = (t.effects[F('EFFECT')] || 0) + num(In('CHANGE')); return 'next'; }
    if (op === 'looks_seteffectto') { t.effects = t.effects || {}; t.effects[F('EFFECT')] = num(In('VALUE')); return 'next'; }
    if (op === 'looks_cleargraphiceffects') { t.effects = {}; return 'next'; }
    if (op === 'looks_show') { t.visible = true; return 'next'; }
    if (op === 'looks_hide') { t.visible = false; return 'next'; }
    if (op === 'looks_gotofrontback') {
      const max = Math.max(...this.project.targets.map((s) => s.layerOrder || 0));
      t.layerOrder = F('FRONT_BACK') === 'back' ? 1 : max + 1;
      return 'next';
    }
    if (op === 'looks_goforwardbackwardlayers') {
      const n = num(In('NUM'));
      t.layerOrder = (t.layerOrder || 1) + (F('FORWARD_BACKWARD') === 'backward' ? -n : n);
      return 'next';
    }

    if (op === 'sound_play' || op === 'sound_playuntildone') {
      const dur = this.playSound(t, In('SOUND_MENU'));
      if (op === 'sound_playuntildone' && dur) { th.waitUntil = performance.now() + dur * 1000; return 'wait'; }
      return 'next';
    }
    if (op === 'sound_stopallsounds') { this.stopSounds(); return 'next'; }
    if (op === 'sound_changevolumeby') { t.volume = Math.max(0, Math.min(100, (t.volume || 100) + num(In('VOLUME')))); return 'next'; }
    if (op === 'sound_setvolumeto') { t.volume = Math.max(0, Math.min(100, num(In('VOLUME')))); return 'next'; }
    if (op === 'sound_cleareffects' || op === 'sound_changeeffectby' || op === 'sound_seteffectto') return 'next';

    if (op === 'event_broadcast') { this.broadcast(str(In('BROADCAST_INPUT'))); return 'next'; }
    if (op === 'event_broadcastandwait') { this.broadcast(str(In('BROADCAST_INPUT'))); th.yieldNow = true; return 'next'; }

    if (op === 'control_wait') { th.waitUntil = performance.now() + num(In('DURATION')) * 1000; return 'wait'; }
    if (op === 'control_wait_until') {
      if (!bool(In('CONDITION'))) { th.waitUntil = performance.now() + 16; return 'wait-retry'; }
      return 'next';
    }
    if (op === 'control_forever') {
      const sub = this.subId(b, 'SUBSTACK');
      frame.phase = 'loop';
      frame.afterSub = 'loop';
      if (sub) th.stack.push({ id: sub, phase: 'enter' });
      th.yieldNow = true;
      return 'push';
    }
    if (op === 'control_repeat') {
      frame.left = frame.left == null ? Math.round(num(In('TIMES'))) : frame.left;
      if (frame.left > 0) {
        frame.left--;
        const sub = this.subId(b, 'SUBSTACK');
        frame.afterSub = 'enter';
        if (sub) th.stack.push({ id: sub, phase: 'enter' });
        th.yieldNow = true;
        return 'push';
      }
      return 'next';
    }
    if (op === 'control_repeat_until') {
      if (!bool(In('CONDITION'))) {
        const sub = this.subId(b, 'SUBSTACK');
        frame.afterSub = 'enter';
        if (sub) th.stack.push({ id: sub, phase: 'enter' });
        th.yieldNow = true;
        return 'push';
      }
      return 'next';
    }
    if (op === 'control_if' || op === 'control_if_else') {
      const ok = bool(In('CONDITION'));
      const sub = this.subId(b, ok ? 'SUBSTACK' : 'SUBSTACK2');
      if (sub) {
        frame.afterSub = 'next';
        th.stack.push({ id: sub, phase: 'enter' });
        return 'push';
      }
      return 'next';
    }
    if (op === 'control_stop') {
      const opt = F('STOP_OPTION');
      if (opt === 'all') this.stop();
      else if (opt === 'this script') th.done = true;
      else this.threads.forEach((o) => { if (o !== th && o.target === t) o.done = true; });
      return 'next';
    }
    if (op === 'control_create_clone_of') {
      this.createClone(t, str(In('CLONE_OPTION')));
      return 'next';
    }
    if (op === 'control_delete_this_clone') {
      if (t._isClone) {
        this.clones = this.clones.filter((c) => c !== t);
        this.threads.forEach((o) => { if (o.target === t) o.done = true; });
        th.done = true;
      }
      return 'next';
    }

    if (op === 'sensing_askandwait') {
      this.ask = { question: str(In('QUESTION')), target: t };
      if (this.onAsk) this.onAsk(this.ask);
      th._asking = true;
      th.waitUntil = performance.now() + 1e12;
      return 'wait';
    }
    if (op === 'sensing_resettimer') { this.timers.start = performance.now(); return 'next'; }
    if (op === 'sensing_setdragmode') { t.draggable = F('DRAG_MODE') === 'draggable'; return 'next'; }

    if (op === 'data_setvariableto') { this.setVar(t, F('VARIABLE'), In('VALUE')); return 'next'; }
    if (op === 'data_changevariableby') { this.setVar(t, F('VARIABLE'), num(this.getVar(t, F('VARIABLE'))) + num(In('VALUE'))); return 'next'; }
    if (op === 'data_showvariable') { this.showMon(F('VARIABLE'), true, false); return 'next'; }
    if (op === 'data_hidevariable') { this.showMon(F('VARIABLE'), false, false); return 'next'; }
    if (op === 'data_addtolist') { this.getList(t, F('LIST')).push(In('ITEM')); return 'next'; }
    if (op === 'data_deleteoflist') {
      const list = this.getList(t, F('LIST'));
      const idx = this.listIndex(In('INDEX'), list.length);
      if (idx >= 0) list.splice(idx, 1);
      return 'next';
    }
    if (op === 'data_deletealloflist') { this.getList(t, F('LIST')).length = 0; return 'next'; }
    if (op === 'data_insertatlist') {
      const list = this.getList(t, F('LIST'));
      const idx = this.listIndex(In('INDEX'), list.length + 1);
      if (idx >= 0) list.splice(idx, 0, In('ITEM'));
      return 'next';
    }
    if (op === 'data_replaceitemoflist') {
      const list = this.getList(t, F('LIST'));
      const idx = this.listIndex(In('INDEX'), list.length);
      if (idx >= 0) list[idx] = In('ITEM');
      return 'next';
    }
    if (op === 'data_showlist') { this.showMon(F('LIST'), true, true); return 'next'; }
    if (op === 'data_hidelist') { this.showMon(F('LIST'), false, true); return 'next'; }

    if (op === 'pen_clear') { this.penCtx.clearRect(0, 0, STAGE_W, STAGE_H); return 'next'; }
    if (op === 'pen_stamp') { this.stamp(t); return 'next'; }
    if (op === 'pen_penDown') { t._pen = t._pen || defaultPen(); t._pen.down = true; this.dot(t); return 'next'; }
    if (op === 'pen_penUp') { t._pen = t._pen || defaultPen(); t._pen.down = false; return 'next'; }
    if (op === 'pen_setPenColorToColor') { t._pen = t._pen || defaultPen(); t._pen.color = str(In('COLOR')) || '#000'; return 'next'; }
    if (op === 'pen_changePenSizeBy') { t._pen = t._pen || defaultPen(); t._pen.size = Math.max(1, t._pen.size + num(In('SIZE'))); return 'next'; }
    if (op === 'pen_setPenSizeTo') { t._pen = t._pen || defaultPen(); t._pen.size = Math.max(1, num(In('SIZE'))); return 'next'; }
    if (op === 'pen_changePenColorParamBy' || op === 'pen_setPenColorParamTo') return 'next';

    if (op === 'procedures_call') {
      const proto = this.findProc(t, b.mutation?.proccode);
      if (proto?.defNext) {
        th.stack.push({ id: proto.defNext, phase: 'enter' });
        return 'push';
      }
      return 'next';
    }
    return 'next';
  }

  evalInput(th, input) {
    if (!input) return '';
    const inner = input[1];
    if (inner == null) return '';
    if (Array.isArray(inner)) {
      const k = inner[0];
      if (k === 12) return this.getVar(th.target, inner[1]);
      if (k === 13) return this.getList(th.target, inner[1]).join(' ');
      return inner[1];
    }
    if (typeof inner === 'string') return this.evalBlock(th, inner);
    return inner;
  }

  evalBlock(th, id) {
    const t = th.target;
    const b = t.blocks[id];
    if (!b) return '';
    const In = (name) => this.evalInput(th, b.inputs[name]);
    const F = (name) => b.fields[name]?.[0];
    const op = b.opcode;
    if (b.shadow && b.fields) return Object.values(b.fields)[0]?.[0];
    if (op === 'operator_add') return num(In('NUM1')) + num(In('NUM2'));
    if (op === 'operator_subtract') return num(In('NUM1')) - num(In('NUM2'));
    if (op === 'operator_multiply') return num(In('NUM1')) * num(In('NUM2'));
    if (op === 'operator_divide') return num(In('NUM1')) / (num(In('NUM2')) || 1);
    if (op === 'operator_mod') return ((num(In('NUM1')) % num(In('NUM2'))) + num(In('NUM2'))) % (num(In('NUM2')) || 1);
    if (op === 'operator_gt') return num(In('OPERAND1')) > num(In('OPERAND2'));
    if (op === 'operator_lt') return num(In('OPERAND1')) < num(In('OPERAND2'));
    if (op === 'operator_equals') return eq(In('OPERAND1'), In('OPERAND2'));
    if (op === 'operator_and') return bool(In('OPERAND1')) && bool(In('OPERAND2'));
    if (op === 'operator_or') return bool(In('OPERAND1')) || bool(In('OPERAND2'));
    if (op === 'operator_not') return !bool(In('OPERAND'));
    if (op === 'operator_join') return str(In('STRING1')) + str(In('STRING2'));
    if (op === 'operator_letter_of') return str(In('STRING')).charAt(Math.round(num(In('LETTER'))) - 1) || '';
    if (op === 'operator_length') return str(In('STRING')).length;
    if (op === 'operator_contains') return str(In('STRING1')).toLowerCase().includes(str(In('STRING2')).toLowerCase());
    if (op === 'operator_random') {
      const a = num(In('FROM')), c = num(In('TO'));
      const lo = Math.min(a, c), hi = Math.max(a, c);
      if (Number.isInteger(lo) && Number.isInteger(hi)) return lo + Math.floor(Math.random() * (hi - lo + 1));
      return lo + Math.random() * (hi - lo);
    }
    if (op === 'operator_round') return Math.round(num(In('NUM')));
    if (op === 'operator_mathop') return mathOp(F('OPERATOR'), num(In('NUM')));
    if (op === 'motion_xposition') return t.x;
    if (op === 'motion_yposition') return t.y;
    if (op === 'motion_direction') return t.direction;
    if (op === 'looks_size') return t.size;
    if (op === 'looks_costumenumbername') return F('NUMBER_NAME') === 'name' ? t.costumes[t.currentCostume]?.name : t.currentCostume + 1;
    if (op === 'looks_backdropnumbername') {
      const s = this.stage();
      return F('NUMBER_NAME') === 'name' ? s.costumes[s.currentCostume]?.name : s.currentCostume + 1;
    }
    if (op === 'sound_volume') return t.volume ?? 100;
    if (op === 'sensing_answer') return this.answer;
    if (op === 'sensing_mousex') return this.mouse.x;
    if (op === 'sensing_mousey') return this.mouse.y;
    if (op === 'sensing_mousedown') return this.mouse.down;
    if (op === 'sensing_keypressed') return this.keys.has(String(In('KEY_OPTION')).toLowerCase()) || this.keys.has(str(In('KEY_OPTION')));
    if (op === 'sensing_timer') return (performance.now() - this.timers.start) / 1000;
    if (op === 'sensing_loudness') return 0;
    if (op === 'sensing_dayssince2000') return (Date.now() - Date.UTC(2000, 0, 1)) / 86400000;
    if (op === 'sensing_username') return 'player';
    if (op === 'sensing_current') return currentOf(F('CURRENTMENU'));
    if (op === 'sensing_touchingobject') return this.touching(t, str(In('TOUCHINGOBJECTMENU')));
    if (op === 'sensing_distanceto') {
      const p = this.gotoPoint(t, In('DISTANCETOMENU'));
      return Math.hypot(p.x - t.x, p.y - t.y);
    }
    if (op === 'sensing_of') return this.attrOf(F('PROPERTY'), str(In('OBJECT')));
    if (op === 'data_itemoflist') {
      const list = this.getList(t, F('LIST'));
      const idx = this.listIndex(In('INDEX'), list.length);
      return idx >= 0 ? list[idx] : '';
    }
    if (op === 'data_itemnumoflist') {
      const list = this.getList(t, F('LIST'));
      const want = str(In('ITEM')).toLowerCase();
      return list.findIndex((x) => str(x).toLowerCase() === want) + 1;
    }
    if (op === 'data_lengthoflist') return this.getList(t, F('LIST')).length;
    if (op === 'data_listcontainsitem') {
      const list = this.getList(t, F('LIST'));
      const want = str(In('ITEM')).toLowerCase();
      return list.some((x) => str(x).toLowerCase() === want);
    }
    if (op === 'argument_reporter_string_number' || op === 'argument_reporter_boolean') return F('VALUE');
    if (op === 'sensing_keyoptions') return F('KEY_OPTION');
    return '';
  }

  subId(b, name) {
    const inp = b.inputs[name];
    return inp && typeof inp[1] === 'string' ? inp[1] : null;
  }

  stage() { return this.project.targets.find((t) => t.isStage); }

  wrapDir(d) {
    let x = d % 360;
    if (x > 180) x -= 360;
    if (x <= -180) x += 360;
    return x;
  }

  setXY(t, x, y) {
    const ox = t.x, oy = t.y;
    t.x = x; t.y = y;
    if (t._pen?.down) this.stroke(ox, oy, x, y, t._pen);
  }

  move(t, steps) {
    const rad = (90 - (t.direction || 90)) * Math.PI / 180;
    this.setXY(t, t.x + Math.cos(rad) * steps, t.y + Math.sin(rad) * steps);
  }

  bounce(t) {
    const box = this.spriteBox(t);
    if (box.r > 240) { t.x -= box.r - 240; t.direction = this.wrapDir(180 - t.direction); }
    if (box.l < -240) { t.x += -240 - box.l; t.direction = this.wrapDir(180 - t.direction); }
    if (box.t > 180) { t.y -= box.t - 180; t.direction = this.wrapDir(-t.direction); }
    if (box.b < -180) { t.y += -180 - box.b; t.direction = this.wrapDir(-t.direction); }
  }

  gotoPoint(t, name) {
    const n = menuValue(str(name));
    if (n === '_random_') return { x: Math.random() * 480 - 240, y: Math.random() * 360 - 180 };
    if (n === '_mouse_') return { x: this.mouse.x, y: this.mouse.y };
    const spr = this.project.targets.find((s) => s.name === name || s.name === n);
    if (spr) return { x: spr.x || 0, y: spr.y || 0 };
    return { x: t.x, y: t.y };
  }

  switchCostume(t, name) {
    const n = str(name);
    const idx = t.costumes.findIndex((c) => c.name === n);
    if (idx >= 0) t.currentCostume = idx;
    else {
      const i = Math.round(num(n)) - 1;
      if (i >= 0 && i < t.costumes.length) t.currentCostume = i;
    }
  }

  touching(t, name) {
    const n = menuValue(name);
    const box = this.spriteBox(t);
    if (n === '_mouse_') return this.mouse.x >= box.l && this.mouse.x <= box.r && this.mouse.y >= box.b && this.mouse.y <= box.t;
    if (n === '_edge_') return box.r > 240 || box.l < -240 || box.t > 180 || box.b < -180;
    const o = this.project.targets.find((s) => s.name === name);
    if (!o || o === t) return false;
    const b = this.spriteBox(o);
    return box.l < b.r && box.r > b.l && box.b < b.t && box.t > b.b;
  }

  findVarRec(t, name) {
    for (const [id, rec] of Object.entries(t.variables || {})) if (rec[0] === name || id === name) return rec;
    const st = this.stage();
    for (const [id, rec] of Object.entries(st.variables || {})) if (rec[0] === name || id === name) return rec;
    return null;
  }
  getVar(t, name) { return this.findVarRec(t, name)?.[1] ?? 0; }
  setVar(t, name, val) {
    const rec = this.findVarRec(t, name);
    if (rec) rec[1] = val;
  }
  getList(t, name) {
    for (const rec of Object.values(t.lists || {})) if (rec[0] === name) return rec[1];
    const st = this.stage();
    for (const rec of Object.values(st.lists || {})) if (rec[0] === name) return rec[1];
    return [];
  }
  listIndex(idx, len) {
    if (str(idx).toLowerCase() === 'last') return len - 1;
    if (str(idx).toLowerCase() === 'random') return Math.floor(Math.random() * len);
    const i = Math.round(num(idx)) - 1;
    return i >= 0 && i < len ? i : -1;
  }

  showMon(name, show, list) {
    this.project.monitors = this.project.monitors || [];
    let m = this.project.monitors.find((x) => x.params?.VARIABLE === name || x.params?.LIST === name);
    if (!m) {
      m = { id: name, mode: list ? 'list' : 'default', opcode: list ? 'data_listcontents' : 'data_variable', params: list ? { LIST: name } : { VARIABLE: name }, spriteName: null, value: 0, width: 0, height: 0, x: 10, y: 10 + this.project.monitors.length * 28, visible: true, sliderMin: 0, sliderMax: 100, isDiscrete: true };
      this.project.monitors.push(m);
    }
    m.visible = show;
  }

  collectMonitors() {
    const t = this.project.targets[0];
    return (this.project.monitors || []).filter((m) => m.visible).map((m) => ({
      ...m,
      value: m.params.LIST ? this.getList(t, m.params.LIST).join(', ') : this.getVar(t, m.params.VARIABLE),
    }));
  }

  broadcast(name) {
    this.startHats('event_whenbroadcastreceived', (b) => b.fields.BROADCAST_OPTION?.[0] === name);
    this.start();
  }

  createClone(from, name) {
    const src = menuValue(name) === '_myself_' ? from : this.project.targets.find((s) => s.name === name) || from;
    const clone = { ...src, variables: { ...src.variables }, lists: JSON.parse(JSON.stringify(src.lists || {})), _isClone: true, _bubble: null, _pen: src._pen ? { ...src._pen } : defaultPen(), layerOrder: (src.layerOrder || 1) + 0.01 };
    this.clones.push(clone);
    for (const [id, b] of Object.entries(clone.blocks || {})) {
      if (b.topLevel && b.opcode === 'control_start_as_clone') this.threads.push(this.makeThread(clone, id));
    }
  }

  findProc(t, proccode) {
    for (const [id, b] of Object.entries(t.blocks || {})) {
      if (b.opcode === 'procedures_definition') {
        const protoId = b.inputs.custom_block?.[1];
        const proto = t.blocks[protoId];
        if (proto?.mutation?.proccode === proccode) return { defNext: b.next };
      }
    }
    return null;
  }

  attrOf(prop, object) {
    const t = object === '_stage_' ? this.stage() : this.project.targets.find((s) => s.name === object);
    if (!t) return 0;
    if (prop === 'x position') return t.x || 0;
    if (prop === 'y position') return t.y || 0;
    if (prop === 'direction') return t.direction || 90;
    if (prop === 'costume #' || prop === 'backdrop #') return (t.currentCostume || 0) + 1;
    if (prop === 'costume name' || prop === 'backdrop name') return t.costumes[t.currentCostume]?.name;
    if (prop === 'size') return t.size || 100;
    if (prop === 'volume') return t.volume || 100;
    return this.getVar(t, prop);
  }

  ensureAudio() {
    if (!this.audio) this.audio = new (window.AudioContext || window.webkitAudioContext)();
    if (this.audio.state === 'suspended') this.audio.resume();
    return this.audio;
  }

  playSound(t, name) {
    const s = t.sounds.find((x) => x.name === name) || t.sounds[0];
    if (!s?._data) return 0;
    try {
      const { samples, sampleRate } = decodeWav(s._data);
      const ctx = this.ensureAudio();
      const buf = ctx.createBuffer(1, samples.length, sampleRate);
      buf.getChannelData(0).set(samples);
      const src = ctx.createBufferSource();
      const gain = ctx.createGain();
      gain.gain.value = (t.volume ?? 100) / 100;
      src.buffer = buf;
      src.connect(gain).connect(ctx.destination);
      src.start();
      this._sources = this._sources || [];
      this._sources.push(src);
      return samples.length / sampleRate;
    } catch {
      return 0;
    }
  }

  stopSounds() {
    for (const s of this._sources || []) { try { s.stop(); } catch {} }
    this._sources = [];
  }

  submitAnswer(value) {
    this.answer = value;
    this.ask = null;
    if (this.onAsk) this.onAsk(null);
    if (this.vm) {
      this.vm.runtime.emit('ANSWER', value);
      return;
    }
    for (const th of this.threads) {
      if (th._asking) { th._asking = false; th.waitUntil = 0; }
    }
  }

  stroke(x0, y0, x1, y1, pen) {
    const p = this.penCtx;
    p.strokeStyle = pen.color;
    p.lineWidth = pen.size;
    p.lineCap = 'round';
    p.beginPath();
    p.moveTo(240 + x0, 180 - y0);
    p.lineTo(240 + x1, 180 - y1);
    p.stroke();
  }
  dot(t) {
    const p = this.penCtx;
    p.fillStyle = t._pen.color;
    p.beginPath();
    p.arc(240 + t.x, 180 - t.y, t._pen.size / 2, 0, Math.PI * 2);
    p.fill();
  }
  stamp(t) {
    this.drawSprite(this.penCtx, t);
  }

  draw() {
    if (this.vm && this.renderer) {
      this.syncProjectToVm();
      this.renderer.draw();
      return;
    }
    if (!this.ctx) return;
    const g = this.ctx;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, STAGE_W, STAGE_H);
    const stage = this.stage();
    if (stage) this.drawSprite(g, stage, true);
    g.drawImage(this.pen, 0, 0);
    for (const t of this.drawableTargets()) {
      if (t.isStage) continue;
      if (t.visible === false) continue;
      this.drawSprite(g, t, false);
    }
    for (const t of this.drawableTargets()) {
      if (t._bubble?.text) this.drawBubble(g, t);
    }
  }

  drawSprite(g, t, isStage) {
    const c = t.costumes[t.currentCostume] || t.costumes[0];
    if (!c) return;
    const img = this.images.get(c.md5ext || c.assetId);
    if (!img) {
      if (isStage) { g.fillStyle = '#fff'; g.fillRect(0, 0, STAGE_W, STAGE_H); }
      return;
    }
    const res = c.bitmapResolution || 1;
    const scale = (t.size || 100) / 100;
    const w = img.naturalWidth / res;
    const h = img.naturalHeight / res;
    const rcx = (c.rotationCenterX ?? img.naturalWidth / 2) / res;
    const rcy = (c.rotationCenterY ?? img.naturalHeight / 2) / res;
    const cx = isStage ? STAGE_W / 2 : 240 + t.x;
    const cy = isStage ? STAGE_H / 2 : 180 - t.y;
    g.save();
    g.translate(cx, cy);
    if (!isStage) {
      const style = t.rotationStyle || 'all around';
      if (style === 'left-right') {
        if (this.wrapDir(t.direction || 90) < 0) g.scale(-1, 1);
      } else if (style !== "don't rotate") {
        g.rotate(((t.direction || 90) - 90) * Math.PI / 180);
      }
    }
    const ghost = t.effects?.ghost ?? 0;
    g.globalAlpha = Math.max(0, 1 - ghost / 100);
    if (t.effects?.brightness) g.filter = `brightness(${100 + t.effects.brightness}%)`;
    g.drawImage(img, -rcx * scale, -rcy * scale, w * scale, h * scale);
    g.restore();
  }

  drawBubble(g, t) {
    const text = String(t._bubble.text);
    g.font = '14px Helvetica, Arial, sans-serif';
    const w = Math.min(220, Math.max(48, g.measureText(text).width + 16));
    const x = 240 + t.x + 20;
    const y = 180 - t.y - 50;
    g.fillStyle = '#fff';
    g.strokeStyle = '#ccc';
    roundRect(g, x, y, w, 28, t._bubble.think ? 14 : 6);
    g.fill();
    g.stroke();
    g.fillStyle = '#111';
    g.textBaseline = 'alphabetic';
    g.fillText(text, x + 8, y + 18, w - 12);
  }
}

export function svgForImage(svg) {
  if (!svg || typeof svg !== 'string') return svg;
  let s = svg;
  s = s.replace(/font-family=(["'])[^"']*\1/gi, 'font-family=$1sans-serif$1');
  s = s.replace(/<text\b([^>]*?)>/gi, (m, attrs) => {
    if (/font-family=/i.test(attrs)) return m;
    return `<text font-family="sans-serif"${attrs}>`;
  });
  return s;
}

function defaultPen() { return { down: false, color: '#000000', size: 1 }; }

function mathOp(op, n) {
  switch (op) {
    case 'abs': return Math.abs(n);
    case 'floor': return Math.floor(n);
    case 'ceiling': return Math.ceil(n);
    case 'sqrt': return Math.sqrt(n);
    case 'sin': return Math.sin(n * Math.PI / 180);
    case 'cos': return Math.cos(n * Math.PI / 180);
    case 'tan': return Math.tan(n * Math.PI / 180);
    case 'asin': return Math.asin(n) * 180 / Math.PI;
    case 'acos': return Math.acos(n) * 180 / Math.PI;
    case 'atan': return Math.atan(n) * 180 / Math.PI;
    case 'ln': return Math.log(n);
    case 'log': return Math.log10(n);
    case 'e ^': return Math.exp(n);
    case '10 ^': return 10 ** n;
    default: return n;
  }
}

function currentOf(part) {
  const d = new Date();
  const p = String(part).toLowerCase();
  if (p === 'year') return d.getFullYear();
  if (p === 'month') return d.getMonth() + 1;
  if (p === 'date') return d.getDate();
  if (p === 'day of week') return d.getDay() + 1;
  if (p === 'hour') return d.getHours();
  if (p === 'minute') return d.getMinutes();
  if (p === 'second') return d.getSeconds();
  return 0;
}

function roundRect(g, x, y, w, h, r) {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

export async function projectToSb3(project) {
  compileProject(project);
  return saveSb3(project);
}

export async function tryTurboWarpVM(canvas, sb3) {
  try {
    const VM = (await import('scratch-vm')).default;
    const Storage = (await import('scratch-storage')).default;
    const vm = new VM();
    const storage = new Storage();
    vm.attachStorage(storage);
    try {
      const Render = (await import('scratch-render')).default;
      const renderer = new Render(canvas);
      vm.attachRenderer(renderer);
    } catch {}
    try {
      const AudioEngine = (await import('scratch-audio')).default;
      vm.attachAudioEngine(new AudioEngine());
    } catch {}
    vm.setCompatibilityMode(false);
    vm.setTurboMode(false);
    await vm.loadProject(sb3);
    vm.start();
    return vm;
  } catch (e) {
    console.warn('scratch-vm unavailable, using built-in TurboWarp-compatible player', e);
    return null;
  }
}
